import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { messages } from "./messages";
import { defaultLanguageFromDevice } from "../config/constants";
import type { SupportedLanguage } from "../types/domain";

type TranslationKey = keyof typeof messages.it;
type Params = Record<string, string | number>;

interface I18nValue {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: TranslationKey, params?: Params) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(value: string, params?: Params): string {
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (_, key: string) => String(params[key] ?? `{${key}}`));
}

function readInitialLanguage(): SupportedLanguage {
  const stored = localStorage.getItem("tripai.language");
  if (stored === "it" || stored === "en" || stored === "es") return stored;
  return defaultLanguageFromDevice();
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(readInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem("tripai.language", language);
  }, [language]);

  const value = useMemo<I18nValue>(() => ({
    language,
    setLanguage(next) {
      localStorage.setItem("tripai.language", next);
      document.documentElement.lang = next;
      setLanguageState(next);
    },
    t(key, params) {
      const dict = messages[language] as Record<string, string>;
      const fallback = messages.en as Record<string, string>;
      return interpolate(dict[key] || fallback[key] || String(key), params);
    }
  }), [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error("I18nProvider missing");
  return context;
}
